/**
 * Strategy picker — the custom select modal for the Strategies view.
 * Design: features/pulsarix-strategy-select-modal.html (light + dark via the
 * global.css tokens only; no hard-coded colours here).
 *
 * The selection is a DRAFT until "Show on chart", so browsing and filtering
 * never flicker the chart behind the modal. Rows sort by what's live right now:
 * running → pending → watching → paused/stalled; retired strategies hide behind
 * their own chip.
 */
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import { setSelectedStrategies, setStrategyPickerOpen } from "@/state/slices/strategiesSlice";
import type { StrategySummary } from "@/api/types";
import { Sparkline, StatusPills } from "./StatusPill";
import styles from "./Strategies.module.css";

type Filter =
  | "all" | "active" | "running" | "pending" | "watching"
  | "off" | "attention" | "selected" | "retired";

const ORDER: Record<string, number> = {
  running: 0, pending: 1, watching: 2, off: 3, paused: 4, stalled: 4, offline: 4, retired: 5,
};

function sortKey(s: StrategySummary): number {
  if (s.status.running > 0) return 0;
  if (s.status.pending > 0) return 1;
  return ORDER[s.status.state] ?? 3;
}

function group(s: StrategySummary): string {
  if (s.status.state === "retired") return "Retired";
  if (s.status.running > 0 || s.status.pending > 0) return "Active now";
  if (s.status.state === "watching") return "Watching";
  // Registered but not enabled yet — inactive by choice, not a problem.
  if (s.status.state === "off") return "Not enabled";
  return "Needs attention";
}

const fmt = (v: number) => `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(v).toFixed(2)}`;
const tone = (v: number) => (v > 0 ? styles.up : v < 0 ? styles.down : "");

export function StrategyPicker() {
  const dispatch = useAppDispatch();
  const open = useAppSelector((s) => s.strategies.pickerOpen);
  const strategies = useAppSelector((s) => s.strategies.strategies);
  const selected = useAppSelector((s) => s.strategies.selectedIds);
  const runner = useAppSelector((s) => s.strategies.runner);
  const online = useAppSelector((s) => s.strategies.online);

  const [draft, setDraft] = useState<string[]>(selected);
  const [filter, setFilter] = useState<Filter>("all");
  const [sym, setSym] = useState<"All" | "XAUUSD" | "BTCUSD">("All");
  const [q, setQ] = useState("");

  useEffect(() => {
    if (open) setDraft(selected);
  }, [open, selected]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dispatch(setStrategyPickerOpen(false));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, dispatch]);

  const inSymbol = useMemo(
    () => strategies.filter((s) => sym === "All" || s.symbols.includes(sym)),
    [strategies, sym],
  );
  const live = inSymbol.filter((s) => s.status.state !== "retired");
  const counts: Record<Filter, number> = {
    all: live.length,
    active: live.filter((s) => s.status.running > 0 || s.status.pending > 0).length,
    running: live.filter((s) => s.status.running > 0).length,
    pending: live.filter((s) => s.status.pending > 0).length,
    watching: live.filter((s) => s.status.state === "watching").length,
    off: live.filter((s) => s.status.state === "off").length,
    attention: live.filter((s) => ["paused", "stalled", "offline"].includes(s.status.state)).length,
    // Scoped to the chosen instrument like every other chip — a BTC pick must
     // not show up in a count while XAUUSD is selected.
    selected: inSymbol.filter((s) => draft.includes(s.id)).length,
    retired: inSymbol.filter((s) => s.status.state === "retired").length,
  };

  // Switching instrument can empty the active filter (e.g. "Running" with no
  // BTC positions) — fall back to All rather than showing an empty list.
  useEffect(() => {
    if (filter !== "all" && counts[filter] === 0) setFilter("all");
  }, [filter, counts]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return inSymbol
      .filter((s) => {
        if (needle && !`${s.name} ${s.symbols.join(" ")} ${s.description} ${s.timeframe}`.toLowerCase().includes(needle)) {
          return false;
        }
        const retired = s.status.state === "retired";
        if (filter === "retired") return retired;
        if (retired) return false;
        switch (filter) {
          case "active": return s.status.running > 0 || s.status.pending > 0;
          case "running": return s.status.running > 0;
          case "pending": return s.status.pending > 0;
          case "watching": return s.status.state === "watching";
          case "off": return s.status.state === "off";
          case "attention": return ["paused", "stalled", "offline"].includes(s.status.state);
          case "selected": return draft.includes(s.id);
          default: return true;
        }
      })
      .sort((a, b) => sortKey(a) - sortKey(b) || b.status.running - a.status.running || a.name.localeCompare(b.name));
  }, [inSymbol, q, filter, draft]);

  if (!open) return null;

  const toggle = (id: string) => {
    setDraft(draft.includes(id) ? draft.filter((x) => x !== id) : [...draft, id]);
  };

  // With no cap, picking groups by hand would be tedious. "Shown" follows the
  // current filter + instrument; "All" takes every strategy that can trade
  // (retired ones never will, so they are left out).
  const shownIds = rows.map((s) => s.id);
  const allIds = strategies.filter((s) => s.status.state !== "retired").map((s) => s.id);
  const everythingPicked = allIds.length > 0 && allIds.every((id) => draft.includes(id));
  const toggleAll = () => setDraft(everythingPicked ? [] : allIds);
  const allShownPicked = shownIds.length > 0 && shownIds.every((id) => draft.includes(id));
  const toggleShown = () => {
    setDraft(
      allShownPicked
        ? draft.filter((id) => !shownIds.includes(id))
        : Array.from(new Set([...draft, ...shownIds])),
    );
  };

  const apply = () => {
    dispatch(setSelectedStrategies(draft));
    dispatch(setStrategyPickerOpen(false));
  };

  // Chips are per-instrument: one with nothing behind it for this symbol is
  // noise, so only All (the way back) and the active chip survive an empty count.
  const chips: [Filter, string][] = (
    [
      ["all", "All"], ["active", "Active"], ["running", "Running"], ["pending", "Pending"],
      ["watching", "Watching"], ["off", "Not enabled"], ["attention", "Needs attention"],
      ["selected", "Selected"], ["retired", "Retired"],
    ] as [Filter, string][]
  ).filter(([k]) => k === "all" || k === filter || counts[k] > 0);

  let lastGroup = "";
  const draftStrategies = strategies.filter((s) => draft.includes(s.id));

  return (
    <div className={styles.backdrop} onClick={() => dispatch(setStrategyPickerOpen(false))}>
      <div className={styles.modal} role="dialog" aria-labelledby="strategy-picker-title" onClick={(e) => e.stopPropagation()}>
        <div className={styles.head}>
          <span className={styles.title} id="strategy-picker-title">Strategies</span>
          <span className={styles.runner}>
            <span className={online ? styles.runnerLive : styles.runnerDown} />
            {online ? "Runner live" : "Runner offline"}
            {runner.why ? ` · ${runner.why}` : ""}
          </span>
          <button type="button" className={styles.close} aria-label="Close" onClick={() => dispatch(setStrategyPickerOpen(false))}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        <div className={styles.tools}>
          <label className={styles.search}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20l-3.5-3.5" />
            </svg>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search strategy, symbol or setup…"
              autoComplete="off"
              autoFocus
            />
          </label>
          <div className={styles.seg} role="tablist" aria-label="Symbol">
            {(["All", "XAUUSD", "BTCUSD"] as const).map((s) => (
              <button
                key={s}
                type="button"
                role="tab"
                aria-selected={sym === s}
                className={sym === s ? styles.segOn : ""}
                onClick={() => setSym(s)}
              >
                {s !== "All" ? <span className={s === "XAUUSD" ? styles.icXau : styles.icBtc} /> : null}
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.chips}>
          {chips.map(([k, label]) => (
            <button
              key={k}
              type="button"
              className={`${styles.chip} ${filter === k ? styles.chipOn : ""}`}
              onClick={() => setFilter(k)}
            >
              {k === "running" ? <span className={`${styles.chipDot} ${styles.chipDotRunning}`} /> : null}
              {k === "pending" ? <span className={`${styles.chipDot} ${styles.chipDotPending}`} /> : null}
              {label} <span className={styles.chipN}>{counts[k]}</span>
            </button>
          ))}
        </div>

        <div className={styles.colhead}>
          <span />
          <span>Strategy</span>
          <span>Status</span>
          <span>Open exposure</span>
          <span>Last 30 days</span>
          <span>Equity</span>
        </div>

        <div className={styles.list}>
          {strategies.length === 0 ? (
            <div className={styles.empty}>
              {online ? "No strategies are published yet." : "The strategy runner is offline."}
            </div>
          ) : rows.length === 0 ? (
            <div className={styles.empty}>No strategies match these filters.</div>
          ) : (
            rows.map((s) => {
              const g = group(s);
              const header = g !== lastGroup ? <div className={styles.grp}>{g}</div> : null;
              lastGroup = g;
              const isSel = draft.includes(s.id);
              const dim = ["paused", "stalled", "retired", "offline"].includes(s.status.state);
              const st = s.stats_30d;
              return (
                <div key={s.id}>
                  {header}
                  <div
                    role="checkbox"
                    aria-checked={isSel}
                    tabIndex={0}
                    className={`${styles.row} ${isSel ? styles.rowSel : ""} ${dim ? styles.rowDim : ""}`}
                    style={{ ["--sw" as string]: s.color }}
                    onClick={() => toggle(s.id)}
                    onKeyDown={(e) => {
                      if (e.key === " " || e.key === "Enter") {
                        e.preventDefault();
                        toggle(s.id);
                      }
                    }}
                  >
                    <span className={styles.cb}>
                      <svg viewBox="0 0 24 24" fill="none" aria-hidden>
                        <path d="M5 12l5 5 9-10" />
                      </svg>
                    </span>
                    <div className={styles.name}>
                      <span className={styles.nameT}>
                        <span className={styles.sw} />
                        {s.name}
                      </span>
                      <span className={styles.meta}>
                        {s.symbols.map((sy) => (
                          <span key={sy} className={`${styles.tag} ${sy === "XAUUSD" ? styles.tagXau : styles.tagBtc}`}>{sy}</span>
                        ))}
                        <span className={styles.tag}>{s.timeframe}</span>
                        <span className={styles.desc}>{s.description}</span>
                      </span>
                    </div>
                    <div className={styles.status}>
                      <StatusPills status={s.status} />
                      {s.status.why ? <span className={styles.why}>{s.status.why}</span> : null}
                    </div>
                    <div className={styles.expo}>
                      {s.status.running > 0 ? (
                        <>
                          {s.exposure.map((e) => (
                            <span key={e.side}>
                              <span className={`${styles.side} ${e.side === "buy" ? styles.sideBuy : styles.sideSell}`}>
                                {e.side.toUpperCase()}
                              </span>
                              {e.volume.toFixed(2)}
                            </span>
                          ))}
                          <span className={`${styles.fl} ${tone(s.floating)}`}>{fmt(s.floating)}</span>
                        </>
                      ) : (
                        <span className={styles.none}>
                          {s.status.pending > 0
                            ? `${s.status.pending} order${s.status.pending > 1 ? "s" : ""} resting`
                            : "Flat"}
                        </span>
                      )}
                    </div>
                    <div className={styles.perf}>
                      <span className={styles.k}>P&amp;L</span>
                      <span className={`${styles.v} ${tone(st.net)}`}>{fmt(st.net)}</span>
                      <span className={styles.k}>Win · PF</span>
                      <span className={styles.v}>
                        {st.win_rate != null ? `${st.win_rate}%` : "–"} · {st.profit_factor != null ? st.profit_factor.toFixed(2) : "–"}
                      </span>
                      <span className={styles.k}>Trades</span>
                      <span className={styles.v}>{st.trades}</span>
                    </div>
                    <div className={styles.sparkCell}>
                      <Sparkline points={st.equity} net={st.net} />
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className={styles.foot}>
          <span className={styles.selSw}>
            {draftStrategies.map((s) => (
              <i key={s.id} style={{ background: s.color }} />
            ))}
          </span>
          <span className={styles.footK}>
            <b>{draft.length}</b> selected · shown on the chart together
          </span>
          <div className={styles.footRight}>
            <button type="button" className={styles.btnGhost} onClick={toggleAll} disabled={allIds.length === 0}>
              {everythingPicked ? "Unselect all" : `Select all (${allIds.length})`}
            </button>
            <button type="button" className={styles.btnGhost} onClick={toggleShown} disabled={shownIds.length === 0}>
              {allShownPicked ? "Unselect shown" : `Select shown (${shownIds.length})`}
            </button>
            <button type="button" className={styles.btnGhost} onClick={() => setDraft([])} disabled={draft.length === 0}>
              Clear
            </button>
            <button type="button" className={styles.btnPrimary} disabled={draft.length === 0} onClick={apply}>
              Show on chart
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
